import { ComponentPropsWithRef } from "react";
import styled from "styled-components";
import { Root as RadixRoot, Thumb as RadixThumb } from "@radix-ui/react-switch";
import {
  primary01,
  primary03,
  shade0,
  shade300,
  shade700,
  shade800,
} from "../../styles/tokens";

// TEXT

// STYLES
export const Thumb = styled(RadixThumb)<ThumbStyleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) =>
    $size === "xsmall"
      ? "0.875rem"
      : $size === "small"
        ? "1.125rem"
        : "1.625rem"};
  height: ${({ $size }) =>
    $size === "xsmall"
      ? "0.875rem"
      : $size === "small"
        ? "1.125rem"
        : "1.625rem"};
  background-color: ${shade0};
  border-radius: 3.125rem;
  transition:
    transform 100ms,
    background-color 100ms;
  transform: translateX(0);
  will-change: transform;

  &[data-state="checked"] {
    transform: translateX(
      ${({ $size }) =>
        $size === "xsmall" ? "0.5rem" : $size === "small" ? "0.75rem" : "1rem"}
    );
  }
`;

export const Root = styled(RadixRoot)<RootStyleProps>`
  padding: 0;
  width: ${({ $size }) =>
    $size === "xsmall" ? "1.75rem" : $size === "small" ? "2.25rem" : "3rem"};
  height: ${({ $size }) =>
    $size === "xsmall" ? "1.125rem" : $size === "small" ? "1.5rem" : "2rem"};
  background-color: ${shade700};
  border-radius: 2rem;
  position: relative;
  border: none;
  transition: background-color 100ms;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: 0.1875rem;
  cursor: pointer;

  &[data-disabled] {
    cursor: not-allowed;
    background-color: ${shade800};

    ${Thumb} {
      background-color: ${shade300};
    }
  }

  &:not([data-disabled]) {
    @media (hover: hover) {
      &:hover {
        background-color: ${shade300};

        ${Thumb} {
          transform: translateX(
            ${({ $size }) =>
              $size === "xsmall"
                ? "0.25rem"
                : $size === "small"
                  ? "0.375rem"
                  : "0.5rem"}
          );
        }
      }
    }
  }

  &[data-state="checked"] {
    background-color: ${({ $variant }) =>
      $variant === "subtle" ? shade700 : primary01};

    &[data-disabled] {
      background-color: ${({ $variant }) =>
        $variant === "subtle" ? shade800 : primary03};
    }

    &:not([data-disabled]) {
      @media (hover: hover) {
        &:hover {
          background-color: ${({ $variant }) =>
            $variant === "subtle" ? shade300 : primary03};

          ${Thumb} {
            transform: translateX(
              ${({ $size }) =>
                $size === "xsmall"
                  ? "0.25rem"
                  : $size === "small"
                    ? "0.375rem"
                    : "0.5rem"}
            );
          }
        }
      }
    }
  }

  &:focus-visible {
    outline: 0.125rem solid ${shade0};
    outline-offset: 0.125rem;
  }
`;

// DATA

// TYPES
export type Size = "xsmall" | "small" | "large";
export type Variant = "normal" | "subtle";

export interface Props extends ComponentPropsWithRef<typeof RadixRoot> {
  size?: Size;
  variant?: Variant;
}

type ThumbStyleProps = {
  $size: Size;
};

type RootStyleProps = {
  $size: Size;
  $variant: Variant;
};

// CONSTANTS

// FUNCTIONS

// ASSETS
